import { addImagesToFormData } from './addImagesToFormData';
import { validateAddCartFromData } from './atcViaFromDataValidation';
import { logError } from './logging';

/**
 * Adds nested properties to FormData with proper array notation.
 * @param {Object} options - Configuration options for the FormData conversion.
 * @param {FormData} options.formData - The FormData object to append to.
 * @param {number} options.itemIndex - The item index.
 * @param {string} options.propertyKey - The property key.
 * @param {*} options.value - The value to add.
 */
function addNestedProperty({ formData, itemIndex, propertyKey, value }) {
  if (Array.isArray(value)) {
    // Handle array values (like personalisation choices).
    value.forEach((item) => {
      if (typeof item === 'object' && item !== null) {
        Object.entries(item).forEach(([key, val]) => {
          if (val instanceof File) {
            formData.append(`items[${itemIndex}][properties][${propertyKey}][${key}]`, val, val.name);
          } else {
            formData.append(`items[${itemIndex}][properties][${propertyKey}][${key}]`, val);
          }
        });
      } else {
        formData.append(`items[${itemIndex}][properties][${propertyKey}]`, item);
      }
    });
  } else if (typeof value === 'object' && value !== null) {
    // Handle object values (like base attributes).
    Object.entries(value).forEach(([key, val]) => {
      if (typeof val === 'object' && val !== null) {
        Object.entries(val).forEach(([nestedKey, nestedVal]) => {
          formData.append(`items[${itemIndex}][properties][${propertyKey}][${key}][${nestedKey}]`, nestedVal);
        });
      } else {
        formData.append(`items[${itemIndex}][properties][${propertyKey}][${key}]`, val);
      }
    });
  } else {
    // Handle simple values.
    if (value instanceof File) {
      formData.append(`items[${itemIndex}][properties][${propertyKey}]`, value, value.name);
    } else {
      formData.append(`items[${itemIndex}][properties][${propertyKey}]`, value);
    }
  }
}

/**
 * Converts a cart payload into a FormData object for Shopify Ajax cart submission.
 *
 * @param {Object} options - Configuration options for the FormData conversion.
 * @param {Array} options.items - Array of cart items with properties.
 * @param {string} [options.endpoint='/cart/add.js'] - The cart endpoint to use.
 * @param {Array} options.personalisationChoices - Array of personalisation choices.
 * @param {Array} [options.sections=[]] - Array of sections to update.
 * @param {{url: string, file: File | null, visualiserSlotMode: string, groupId: string, lineItemPropertyKey: string, label: string}[]} [options.visData=[]] - Array of visualiser data.
 * @param {import('../gateway/personalisationData.js').Slot[]} [options.slots=[]] - Array of slots to add.
 * @returns {Promise<FormData>} Promise that resolves to FormData object ready for Shopify Ajax cart submission.
 * @throws {Error} If items array is invalid or missing required properties.
 */
export async function convertCartPayloadToFormData({
  items,
  personalisationChoices,
  sections = [],
  visData = [],
  slots = []
}) {
  // Validate input.
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Items must be a non-empty array');
  }

  // Create FormData object.
  const formData = new FormData();

  // Process each cart item.
  items.forEach((item, itemIndex) => {
    // Validate item structure.
    if (!item.id || typeof item.quantity !== 'number' || item.quantity <= 0) {
      throw new Error(`Invalid item at index ${itemIndex}: missing id or invalid quantity`);
    }

    // Add item ID and quantity.
    formData.append(`items[${itemIndex}][id]`, item.id);
    formData.append(`items[${itemIndex}][quantity]`, item.quantity);

    // Add properties if they exist.
    // Skip File objects here - they will be handled by addImagesToFormData
    if (item.properties && typeof item.properties === 'object') {
      Object.entries(item.properties).forEach(([key, value]) => {
        // Skip File objects - they will be handled in addImagesToFormData
        if (!(value instanceof File)) {
          addNestedProperty({ formData, itemIndex, propertyKey: key, value });
        }
      });
    }
  });

  const containsImageUpload = personalisationChoices.some((choice) => choice.slotType === 'IMAGE_UPLOAD');
  const containsFeaturedImageUrl = items.some((item) => {
    console.log('item.properties', item.properties);

    // Check for _featured_image with or without numbered suffix (_featured_image_1, _featured_image_2, etc).
    return (
      item.properties &&
      Object.keys(item.properties).some((key) => {
        // skip keys that dont start with _featured_image or end with - Visual
        if (!key.startsWith('_featured_image') && !key.endsWith(' - Visual')) return false;
        const value = item.properties[key];
        // Check for both string URLs and File objects (which are already converted from URLs)
        return (typeof value === 'string' && value.includes('http')) || value instanceof File;
      })
    );
  });

  if (containsImageUpload || containsFeaturedImageUrl) {
    await addImagesToFormData({ formData, items, visData });
  }

  if (sections.length > 0) {
    formData.append('sections', sections.join(','));
  }

  validateAddCartFromData({ formData, items, slots });

  return formData;
}
